import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { customerService } from '../../services/customerService';
import { 
  FiPlus, FiEdit, FiTrash2, FiUsers, FiEye,
  FiCheckCircle, FiXCircle, FiAlertCircle, FiSearch
} from 'react-icons/fi';

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await customerService.getAll();
      setCustomers(data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await customerService.delete(id);
        await loadData();
      } catch (error) {
        alert('Error deleting customer: ' + error.message);
      }
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      'perusahaan': 'Perusahaan',
      'personal': 'Personal',
      'instansi': 'Instansi',
      'others': 'Lainnya'
    };
    return types[type] || type;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { label: 'Active', color: 'bg-green-100 text-green-800', icon: FiCheckCircle },
      'inactive': { label: 'Inactive', color: 'bg-red-100 text-red-800', icon: FiXCircle },
      'blocked': { label: 'Blocked', color: 'bg-gray-100 text-gray-800', icon: FiAlertCircle }
    };
    return statusMap[status] || statusMap['active'];
  };

  const filteredCustomers = Array.isArray(customers) 
    ? customers.filter(customer => {
        const matchesSearch = customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              customer.phone?.includes(searchTerm) ||
                              customer.email?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || customer.status === filterStatus;
        return matchesSearch && matchesStatus;
      })
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading customers...</p>
      </div>
    );
  }

  // Stats
  const totalCustomers = Array.isArray(customers) ? customers.length : 0;
  const activeCustomers = Array.isArray(customers) 
    ? customers.filter(c => c.status === 'active').length 
    : 0;
  const inactiveCustomers = Array.isArray(customers) 
    ? customers.filter(c => c.status === 'inactive').length 
    : 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Customers</h1>
        <Link
          to="/customers/new"
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <FiPlus size={20} />
          <span>Add Customer</span>
        </Link>
      </div>

      {/* Stats Cards - Compact */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">Total</p>
          <p className="text-lg font-bold text-[var(--text-primary)]">{totalCustomers}</p>
        </div>
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">Active</p>
          <p className="text-lg font-bold text-green-600">{activeCustomers}</p>
        </div>
        <div className="bg-[var(--bg-primary)] px-3 py-2 rounded-lg shadow-sm border border-[var(--border-color)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">Inactive</p>
          <p className="text-lg font-bold text-red-600">{inactiveCustomers}</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={16} />
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="blocked">Blocked</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-sm border border-[var(--border-color)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--bg-secondary)]">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">Name</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">Phone</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">Email</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">Type</th>
                <th className="px-4 py-2.5 text-center text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">Orders</th>
                <th className="px-4 py-2.5 text-center text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">Status</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-[var(--text-secondary)]">
                    <FiUsers size={40} className="mx-auto mb-2 opacity-50" />
                    <p>No customers found</p>
                    <p className="text-sm">Click "Add Customer" to create one</p>
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => {
                  const statusBadge = getStatusBadge(customer.status);
                  const StatusIcon = statusBadge.icon;
                  return (
                    <tr key={customer.id} className="border-t border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-colors">
                      <td className="px-4 py-2.5 text-sm text-[var(--text-primary)] font-medium">
                        {customer.name}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-[var(--text-secondary)]">
                        {customer.phone}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-[var(--text-secondary)]">
                        {customer.email || '-'}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-[var(--text-secondary)]">
                        {getTypeLabel(customer.type)}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-center text-[var(--text-primary)]">
                        {customer.totalOrders || 0}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`inline-flex items-center space-x-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusBadge.color}`}>
                          <StatusIcon size={12} />
                          <span>{statusBadge.label}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <Link
                            to={`/customers/${customer.id}`}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="View Details"
                          >
                            <FiEye size={16} />
                          </Link>
                          <Link
                            to={`/customers/edit/${customer.id}`}
                            className="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded transition-colors"
                            title="Edit"
                          >
                            <FiEdit size={16} />
                          </Link>
                          <button
                            onClick={() => handleDelete(customer.id)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomerList;